import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const VERTEX_SHADER = `
  void main() {
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

// Animated water/ripple field, tinted toward Tensor Hive's purple/violet
// logo color with a green (#00C805) undertone in the brightest crests.
const FRAGMENT_SHADER = `
  precision highp float;
  uniform float uTime;
  uniform vec2 uMouse;
  uniform vec2 uResolution;

  void main() {
    vec2 uv = gl_FragCoord.xy / uResolution.xy;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= uResolution.x / max(uResolution.y, 1.0);

    float t = uTime * 0.35;

    vec2 c1 = vec2(sin(t * 0.6) * 0.5, cos(t * 0.42) * 0.35);
    vec2 c2 = vec2(-0.55 + sin(t * 0.25) * 0.1, 0.4 + cos(t * 0.3) * 0.15);
    vec2 c3 = uMouse;

    float d1 = length(p - c1);
    float d2 = length(p - c2);
    float d3 = length(p - c3);

    float ripple = 0.0;
    ripple += sin(d1 * 18.0 - uTime * 2.0) * 0.5 * exp(-d1 * 1.05);
    ripple += sin(d2 * 22.0 - uTime * 1.3) * 0.35 * exp(-d2 * 1.25);
    ripple += sin(d3 * 30.0 - uTime * 3.1) * 0.9 * exp(-d3 * 2.4);

    float shade = 0.5 + 0.5 * ripple;

    vec3 dark = vec3(0.035, 0.03, 0.065);
    vec3 purple = vec3(0.47, 0.24, 0.93);
    vec3 green = vec3(0.0, 0.78, 0.02);

    vec3 color = mix(dark, purple, smoothstep(0.15, 0.85, shade));
    color = mix(color, green, smoothstep(0.62, 1.0, shade) * 0.55);

    float vig = smoothstep(1.35, 0.15, length(p));
    color *= vig;

    gl_FragColor = vec4(color, 1.0);
  }
`;

interface WaterShaderBackgroundProps {
  className?: string;
}

/**
 * Full custom Three.js water-ripple shader used as a fixed background layer
 * behind the landing hero. Performant by design: capped DPR, a single
 * requestAnimationFrame loop, ResizeObserver-driven resize, and full
 * geometry/material/renderer disposal on unmount.
 */
export const WaterShaderBackground: React.FC<WaterShaderBackgroundProps> = ({ className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    container.appendChild(renderer.domElement);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const uniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0.4, 0.15) },
      uResolution: { value: new THREE.Vector2(1, 1) },
    };
    const targetMouse = new THREE.Vector2(0.4, 0.15);

    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms,
    });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const resize = () => {
      const width = container.clientWidth || 1;
      const height = container.clientHeight || 1;
      renderer.setSize(width, height, false);
      uniforms.uResolution.value.set(width, height);
    };
    resize();

    const handlePointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const aspect = rect.width / Math.max(rect.height, 1);
      const x = (((e.clientX - rect.left) / rect.width) * 2 - 1) * aspect;
      const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
      targetMouse.set(x, y);
    };

    const clock = new THREE.Clock();
    let rafId = 0;
    const animate = () => {
      if (disposed) return;
      uniforms.uTime.value = clock.getElapsedTime();
      uniforms.uMouse.value.lerp(targetMouse, 0.035);
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    animate();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('pointermove', handlePointerMove);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={containerRef} className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden="true" />;
};

export default WaterShaderBackground;
